// useStoreHoursLogic.ts
import { useState, useCallback, useEffect } from "react";

import { useHandleBusinessEmployees } from "0.Mine/1.Hooks/business/employees";
import { useHandleImageUpload } from "0.Mine/1.Hooks/business/images";
import { useHandleBusinessService } from "0.Mine/1.Hooks/business/services";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { Employee } from "0.Mine/3.Interfaces/Employee";
import { BusinessService } from "0.Mine/3.Interfaces/Services";

export interface StoreHoursLogicReturn {
  employees: Employee[];
  showAddForm: boolean;
  toggleAddForm: () => void;
  handleAddEmployee: (employeeToAdd: Employee) => Promise<void>;
  handleUpdateEmployee: (updatedEmployee: Employee) => void;
  handleDeleteEmployee: (employeeId: number) => void;
  availableServices: BusinessService[];
  isLoading: boolean; // Added isLoading to the interface
  handleFileUpload: (employeeId: number, file: File) => void;
}

export const useStoreHoursLogic = (): StoreHoursLogicReturn => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [availableServices, setAvailableServices] = useState<BusinessService[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Start with loading true
  const { session } = useBusinessContext();
  const { addEmployee, getAllEmployees } = useHandleBusinessEmployees();
  const { getAllServices } = useHandleBusinessService();
  const { uploadImageEmployee } = useHandleImageUpload();
  const retrieveAvailableServices = useCallback(async () => {
    try {
      if (session?.selectedBusiness) {
        const services = await getAllServices(session.selectedBusiness.id);
        setAvailableServices(Array.isArray(services) ? services : []);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error fetching services:", error);
      setAvailableServices([]);
      return false;
    }
  }, [session]);

  const retrieveAvailableEmployees = useCallback(async () => {
    try {
      if (session?.selectedBusiness) {
        const employees = await getAllEmployees(session.selectedBusiness.id);
        console.log("EMPLOYEES:", employees);

        // Ensure employees is always an array
        const employeeArray = Array.isArray(employees) ? employees : [];
        setEmployees(employeeArray);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
      return false;
    }
  }, [session]);

  useEffect(() => {
    const loadData = async () => {
      if (session?.selectedBusiness) {
        setIsLoading(true);
        try {
          // Load both data sources in parallel
          await Promise.all([retrieveAvailableServices(), retrieveAvailableEmployees()]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadData();
  }, [session]);

  const handleAddEmployee = useCallback(
    async (employeeToAdd: Employee) => {
      try {
        if (session?.selectedBusiness) {
          await addEmployee(session.selectedBusiness.id, employeeToAdd);
          // Add to local state after successful API call
          setEmployees((prevEmployees) => [...prevEmployees, employeeToAdd]);

          // Close form with a slight delay for animation
          setTimeout(() => setShowAddForm(false), 300);
        }
      } catch (error) {
        console.error("Error adding employee:", error);
        // Handle error (show notification, etc.)
      }
    },
    [session]
  );

  const handleUpdateEmployee = useCallback((updatedEmployee: Employee) => {
    setEmployees((prevEmployees) =>
      prevEmployees.map((emp) => (emp.id === updatedEmployee.id ? updatedEmployee : emp))
    );
  }, []);

  const handleDeleteEmployee = useCallback((employeeId: number) => {
    setEmployees((prevEmployees) => prevEmployees.filter((emp) => emp.id !== employeeId));
  }, []);

  const toggleAddForm = useCallback(() => {
    setShowAddForm((prev) => !prev);
  }, []);

  const handleFileUpload = (employeeId: number, file: File) => {
    uploadImageEmployee(file, employeeId.toString(), session.selectedBusiness.id.toString());
  };

  return {
    employees,
    showAddForm,
    toggleAddForm,
    handleAddEmployee,
    handleUpdateEmployee,
    handleDeleteEmployee,
    availableServices,
    isLoading,
    handleFileUpload,
  };
};
